import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Bell, ExternalLink, Fuel, Gauge, MapPinOff, Wrench, X } from 'lucide-react';
import { useLiveData } from '@/context/LiveDataContext';
import { useT } from '@/lib/i18n';
import { cn, formatDate } from '@/lib/utils';
import { getAlertSettings } from '@/lib/alertSettingsService';
import { browserNotificationPermission, playAlertSound } from '@/lib/alertNotification';
import type { Alert } from '@/types';

const ICONS: Record<string, typeof Bell> = {
  overspeed: Gauge,
  fuel: Fuel,
  geofence: MapPinOff,
  idle: AlertTriangle,
  engine: Wrench,
};

const SEVERITY: Record<string, { bar: string; dot: string; badge: string; labelKey: string }> = {
  high: { bar: 'border-l-4 border-l-destructive', dot: 'bg-destructive', badge: 'bg-destructive/10 text-destructive', labelKey: 'alertSeverityHigh' },
  medium: { bar: 'border-l-4 border-l-amber-500', dot: 'bg-amber-500', badge: 'bg-amber-500/10 text-amber-600', labelKey: 'alertSeverityMedium' },
  low: { bar: 'border-l-4 border-l-primary', dot: 'bg-primary', badge: 'bg-primary/10 text-primary', labelKey: 'alertSeverityLow' },
};

interface PushItem extends Alert {
  _key: number;
}

const MAX_VISIBLE = 5;
const ALERT_SEEN = new Set<number>(); // persiste entre montajes dentro de la SPA

export default function AlertPushHost() {
  const { alerts } = useLiveData();
  const { t } = useT();
  const navigate = useNavigate();
  const [toasts, setToasts] = useState<PushItem[]>([]);
  const initialized = useRef(false);
  const seen = useRef<Set<number>>(ALERT_SEEN);

  const handleFresh = useCallback(
    async (fresh: Alert[]) => {
      if (!fresh.length) return;
      const settings = await getAlertSettings();

      if (settings.pushInApp) {
        const items: PushItem[] = fresh.map((a) => ({ ...a, _key: a.id || Date.now() + Math.random() }));
        setToasts((prev) => [...items, ...prev].slice(0, MAX_VISIBLE));
        const ttl = Math.max(4, settings.pushDuration) * 1000;
        items.forEach((item) => {
          window.setTimeout(() => {
            setToasts((prev) => prev.filter((x) => x._key !== item._key));
          }, ttl);
        });
      }

      if (settings.pushSound) playAlertSound();

      if (settings.pushBrowser && browserNotificationPermission() === 'granted') {
        fresh.slice(0, 3).forEach((a) => {
          const typeLabel = t(`eventType_${a.rawType ?? ''}`, a.rawType ?? t('alerts'));
          const n = new Notification(`${t('alertPushTitle')}: ${typeLabel}`, {
            body: `${a.message}${a.vehicle ? ` · ${a.vehicle}` : ''}`,
            tag: `fleet-alert-${a.id || Date.now()}`,
            icon: '/icons/icon-192.svg',
          });
          n.onclick = () => {
            window.focus();
            navigate(a.id ? `/event/${a.id}` : '/alerts');
            n.close();
          };
        });
      }
    },
    [navigate, t],
  );

  // Detecta alertas nuevas (solo las generadas tras abrir la app)
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      alerts.forEach((a) => seen.current.add(a.id));
      return;
    }
    const fresh = alerts.filter((a) => !seen.current.has(a.id));
    if (!fresh.length) return;
    fresh.forEach((a) => seen.current.add(a.id));
    void handleFresh(fresh);
  }, [alerts, handleFresh]);

  // Permite probar el aviso desde Configuración de alertas
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as Partial<Alert> | undefined;
      const fake: Alert = {
        id: 0,
        type: detail?.type ?? 'geofence',
        severity: detail?.severity ?? 'medium',
        status: 'open',
        vehicle: detail?.vehicle ?? 'Vehículo de prueba',
        deviceId: 0,
        positionId: null,
        driver: '—',
        message: detail?.message ?? t('alertPushTestMessage', 'Alerta de prueba'),
        time: new Date().toISOString(),
        rawType: detail?.rawType ?? 'geofenceEnter',
      };
      void handleFresh([fake]);
    };
    window.addEventListener('fleet-alert-test', handler as EventListener);
    return () => window.removeEventListener('fleet-alert-test', handler as EventListener);
  }, [handleFresh, t]);

  const dismiss = useCallback((key: number) => {
    setToasts((prev) => prev.filter((x) => x._key !== key));
  }, []);

  const open = useCallback(
    (item: PushItem) => {
      navigate(item.id ? `/event/${item.id}` : '/alerts');
      dismiss(item._key);
    },
    [navigate, dismiss],
  );

  if (!toasts.length) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 top-2 z-[120] flex flex-col items-center gap-2 px-3 sm:inset-x-auto sm:right-4 sm:top-4 sm:items-end sm:px-0">
      {toasts.map((item) => {
        const Icon = ICONS[item.type] || Bell;
        const sev = SEVERITY[item.severity] || SEVERITY.low;
        const typeLabel = t(`eventType_${item.rawType ?? ''}`, item.rawType ?? t('alerts'));
        return (
          <div
            key={item._key}
            className={cn(
              'pointer-events-auto w-full max-w-[380px] overflow-hidden rounded-xl border border-border bg-card shadow-xl',
              sev.bar,
            )}
            role="alert"
          >
            <div className="flex items-start gap-2.5 p-3">
              <div className={cn('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', sev.badge)}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('alertPushTitle')}
                  </span>
                  <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-medium', sev.badge)}>
                    {t(sev.labelKey, item.severity)}
                  </span>
                </div>
                <p className="mt-0.5 text-sm font-medium leading-snug">{typeLabel}</p>
                <p className="mt-0.5 break-words text-xs text-muted-foreground">{item.message}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {item.vehicle}
                  {item.time ? ` · ${formatDate(item.time)}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => dismiss(item._key)}
                className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                aria-label={t('dismiss')}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="flex items-center justify-end gap-1 border-t border-border bg-muted/30 px-2 py-1.5">
              <button
                type="button"
                onClick={() => open(item)}
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {t('alertPushView', 'Ver')}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
